/**
 * The anonymous identifier that lets one device's two sets of answers be
 * paired.
 *
 * Storage is behind an interface, and lives in `storage.ts`. What is left here
 * is the decision: given whatever this device happens to hold, which identifier
 * does this participant have? That is the part with branches, and the part
 * that decides whether somebody who comes back after the event is recognised,
 * so it is the part that is tested directly.
 */
import { isParticipantId, newParticipantId } from '../shared/validation';

/** Somewhere to keep a small string on this device. */
export interface IdStore {
  read(): string | null;
  write(id: string): void;
}

/**
 * Find this device's identifier, or mint one.
 *
 * Anything that is not a well-formed identifier is discarded rather than
 * repaired — a truncated or hand-edited value would be refused by the server
 * on submission, and a participant would have no way to tell why. Minting a
 * fresh one costs them nothing unless they had already answered, and a
 * corrupted value means they cannot prove they had.
 */
export function resolveParticipantId(store: IdStore): string {
  const existing = store.read();
  if (isParticipantId(existing)) return existing;
  const created = newParticipantId();
  store.write(created);
  return created;
}
